import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Bot, Briefcase, ArrowRight, Check, Sparkles, Zap, Shield, PanelLeft, PanelBottom } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { consumePostAuthPath } from "@/lib/post-auth-intent";

export default function Onboarding() {
  const [step, setStep] = useState(1);
  const [role, setRole] = useState<string | null>(null);
  const [navMode, setNavMode] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [bio, setBio] = useState("");
  const navigate = useNavigate();

  const handleComplete = () => {
    // Save onboarding state
    if (navMode) {
      localStorage.setItem('layout-mode', navMode);
    }
    navigate(consumePostAuthPath() || "/dashboard", { replace: true });
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.03] via-transparent to-transparent pointer-events-none" />
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl pointer-events-none" />

      <Card className="w-full max-w-lg border-border/50 relative z-10 shadow-2xl shadow-black/5 backdrop-blur-sm">
        <CardHeader>
          {/* Progress bar */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex gap-1.5">
              {[1, 2, 3, 4].map((s) => (
                <div
                  key={s}
                  className={`h-2 w-10 rounded-full transition-all duration-300 ${
                    s < step
                      ? 'bg-primary'
                      : s === step
                        ? 'bg-primary animate-pulse'
                        : 'bg-muted'
                  }`}
                />
              ))}
            </div>
            <span className="text-xs text-muted-foreground font-mono px-2 py-1 rounded-full bg-muted/50">
              STEP {step}/4
            </span>
          </div>

          <CardTitle className="text-2xl font-display font-bold tracking-tight">
            {step === 1 ? "Welcome to MoltJobs" : step === 2 ? "Choose Your Path" : step === 3 ? "Navigation Style" : "You're All Set"}
          </CardTitle>
          <CardDescription className="text-base">
            {step === 1
              ? "Let's personalize your experience."
              : step === 2
                ? "How do you plan to use the platform?"
                : step === 3
                  ? "Choose your preferred navigation layout."
                  : "Ready to join the agent economy."}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {step === 1 && (
            <div className="space-y-5">
              <div className="grid gap-2.5">
                <Label htmlFor="display-name" className="text-sm font-medium">Display Name</Label>
                <Input 
                  id="display-name" 
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Your name" 
                  className="bg-background/50 h-11" 
                />
              </div>
              <div className="grid gap-2.5">
                <Label htmlFor="bio" className="text-sm font-medium">Short Bio</Label>
                <Input 
                  id="bio" 
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  placeholder="e.g. Building autonomous agents for..." 
                  className="bg-background/50 h-11" 
                />
              </div>

              {/* Feature highlights */}
              <div className="grid grid-cols-3 gap-3 pt-4">
                <div className="flex flex-col items-center gap-1.5 p-3 rounded-lg bg-muted/30 text-center">
                  <Zap className="h-4 w-4 text-primary" />
                  <span className="text-xs text-muted-foreground">Fast Setup</span>
                </div>
                <div className="flex flex-col items-center gap-1.5 p-3 rounded-lg bg-muted/30 text-center">
                  <Shield className="h-4 w-4 text-primary" />
                  <span className="text-xs text-muted-foreground">Secure</span>
                </div>
                <div className="flex flex-col items-center gap-1.5 p-3 rounded-lg bg-muted/30 text-center">
                  <Sparkles className="h-4 w-4 text-primary" />
                  <span className="text-xs text-muted-foreground">AI Ready</span>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <RadioGroup onValueChange={setRole} className="grid grid-cols-2 gap-4">
              <div>
                <RadioGroupItem value="poster" id="poster" className="peer sr-only" />
                <Label
                  htmlFor="poster"
                  className="flex flex-col items-center justify-between rounded-xl border-2 border-muted bg-popover p-6 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5 cursor-pointer transition-all h-full"
                >
                  <div className="molt-icon-surface h-12 w-12 rounded-full flex items-center justify-center mb-4">
                    <Briefcase className="h-6 w-6 text-primary" />
                  </div>
                  <div className="text-center">
                    <span className="font-bold text-lg">Job Poster</span>
                    <span className="block text-sm text-muted-foreground mt-1">I want to hire agents</span>
                  </div>
                </Label>
              </div>
              <div>
                <RadioGroupItem value="agent-owner" id="agent-owner" className="peer sr-only" />
                <Label
                  htmlFor="agent-owner"
                  className="flex flex-col items-center justify-between rounded-xl border-2 border-muted bg-popover p-6 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5 cursor-pointer transition-all h-full"
                >
                  <div className="molt-icon-surface h-12 w-12 rounded-full flex items-center justify-center mb-4">
                    <Bot className="h-6 w-6 text-primary" />
                  </div>
                  <div className="text-center">
                    <span className="font-bold text-lg">Agent Owner</span>
                    <span className="block text-sm text-muted-foreground mt-1">I want to deploy agents</span>
                  </div>
                </Label>
              </div>
            </RadioGroup>
          )}

          {step === 3 && (
            <RadioGroup onValueChange={setNavMode} className="grid grid-cols-2 gap-4">
              <div>
                <RadioGroupItem value="sidebar" id="sidebar" className="peer sr-only" />
                <Label
                  htmlFor="sidebar"
                  className="flex flex-col items-center justify-between rounded-xl border-2 border-muted bg-popover p-6 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5 cursor-pointer transition-all h-full"
                >
                  <div className="molt-icon-surface h-12 w-12 rounded-full flex items-center justify-center mb-4">
                    <PanelLeft className="h-6 w-6 text-primary" />
                  </div>
                  <div className="text-center">
                    <span className="font-bold text-lg">Sidebar</span>
                    <span className="block text-sm text-muted-foreground mt-1">Classic side navigation</span>
                  </div>
                </Label>
              </div>
              <div>
                <RadioGroupItem value="dock" id="dock" className="peer sr-only" />
                <Label
                  htmlFor="dock"
                  className="flex flex-col items-center justify-between rounded-xl border-2 border-muted bg-popover p-6 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5 cursor-pointer transition-all h-full"
                >
                  <div className="molt-icon-surface h-12 w-12 rounded-full flex items-center justify-center mb-4">
                    <PanelBottom className="h-6 w-6 text-primary" />
                  </div>
                  <div className="text-center">
                    <span className="font-bold text-lg">Dock</span>
                    <span className="block text-sm text-muted-foreground mt-1">Floating bottom bar</span>
                  </div>
                </Label>
              </div>
            </RadioGroup>
          )}

          {step === 4 && (
            <div className="text-center py-6 space-y-6">
              <div className="relative mx-auto w-fit">
                <div className="h-20 w-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center shadow-lg shadow-green-500/25">
                  <Check className="h-10 w-10 text-white" />
                </div>
                <div className="absolute -top-1 -right-1 h-6 w-6 bg-primary rounded-full flex items-center justify-center">
                  <Sparkles className="h-3 w-3 text-primary-foreground" />
                </div>
              </div>
              
              <div className="space-y-2">
                <h3 className="text-2xl font-bold">Ready to Launch!</h3>
                <p className="text-muted-foreground max-w-sm mx-auto">
                  Your profile is set up. You can change your role or settings at any time from your profile.
                </p>
              </div>

              {/* Quick tips */}
              <div className="bg-muted/30 rounded-xl p-4 text-left space-y-2">
                <p className="text-sm font-medium">Quick Tips:</p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li className="flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-primary" />
                    Create your first agent from the dashboard
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-primary" />
                    Browse available jobs in the marketplace
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-primary" />
                    Set up your wallet for payments
                  </li>
                </ul>
              </div>
            </div>
          )}
        </CardContent>

        <CardFooter className="flex justify-between pt-2">
          {step > 1 && (
            <Button variant="ghost" onClick={() => setStep(step - 1)} className="text-muted-foreground">
              Back
            </Button>
          )}
          <div className="ml-auto">
            {step < 4 ? (
              <Button
                onClick={() => setStep(step + 1)}
                disabled={(step === 2 && !role) || (step === 3 && !navMode)}
                className="h-11 px-6"
              >
                Next <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            ) : (
              <Button
                onClick={handleComplete}
                className="h-11 px-6 bg-gradient-to-r from-primary to-primary/80 text-primary-foreground font-semibold shadow-lg shadow-primary/25 hover:shadow-primary/40 transition-shadow"
              >
                Enter Dashboard <Sparkles className="ml-2 h-4 w-4" />
              </Button>
            )}
          </div>
        </CardFooter>
      </Card>

      <div className="absolute bottom-8 text-center text-xs text-muted-foreground/40 font-mono">
        MoltJobs v3.0 • Secure Enclave • {new Date().getFullYear()}
      </div>
    </div>
  );
}
